// 189 §B (167 R7) — A DEFINITION THAT SAYS "SHOW IN LISTS" IS A COLUMN.
//
// The three lists (clients through buildEcfg, cases and collective cases
// through buildMatterListCfg) append what this returns after their built-in
// columns: one column per `custom_field` definition that names the entity
// and carries `showInList`, in taxonomy order. The header is the firm's own
// label — DATA, rendered raw (`raw: true`, EntityList) and never through a
// translation key — and the cell renders the stored value the way its type
// reads: a date in the reader's locale, a list code as its option label, a
// yes/no as a tick. Non-sortable (`ns`), like the other derived columns; the
// value lives under `customFields.<code>` and the lists' sorts read top-level
// keys.
//
// Hookless and JSX-bearing, so it is a `.tsx` under src/lib (183 D.3's shape
// for a list model, not a component: the hosts own the state).
import React from "react";
import { localeFor } from "../i18n";
import {
  customFieldDefsFor,
  customFieldLabel,
  normalizeCustomFieldMeta,
} from "./crmDefinitions.mjs";

type Row = Readonly<{ customFields?: Readonly<Record<string, unknown>> | null }>;
/* `r` is typed by React's own FC (an imported type, 183's rule for a callback
   shape): EntityList calls `c.r(it, ctx)` and an FC takes its row as the
   props and tolerates the second argument. */
export type CustomFieldColumn = Readonly<{
  k: string;
  l: string;
  raw: true;
  ns: true;
  r: React.FC<Row>;
}>;

const DASH = "—";
const text = (v: unknown): string => (typeof v === "string" || typeof v === "number" ? String(v) : "");

/* The cell's text for one stored value, by the definition's type — always a
   string, so the renderer has one return type. */
function cellText(v: unknown, type: string, options: ReadonlyMap<string, string>, locale: string): string {
  if (v == null || v === "") return DASH;
  if (type === "bool") return v === true ? "✓" : DASH;
  if (type === "date") {
    const d = new Date(`${text(v)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? text(v) : d.toLocaleDateString(locale);
  }
  if (type === "select") return options.get(text(v)) ?? text(v);
  return text(v) || DASH;
}

export function customFieldColumns(defs: unknown, entity: "client" | "case" | "cc", lang: string): CustomFieldColumn[] {
  const locale = String(localeFor(lang));
  return customFieldDefsFor(defs, entity)
    .filter((d) => normalizeCustomFieldMeta(d.meta).showInList)
    .map((d) => {
      const code = String(d.code);
      const meta = normalizeCustomFieldMeta(d.meta);
      const optionLabel: ReadonlyMap<string, string> = new Map(meta.options.map((o) => [o.code, o.label]));
      const render: React.FC<Row> = (it) => cellText(it.customFields?.[code], meta.type, optionLabel, locale);
      return { k: `cf_${code}`, l: customFieldLabel(d, lang), raw: true, ns: true, r: render };
    });
}
